import camelCase from 'lodash/camelCase'
import find from 'lodash/find'
import flow from 'lodash/flow'
import has from 'lodash/has'
import includes from 'lodash/includes'
import isFunction from 'lodash/isFunction'
import isPlainObject from 'lodash/isPlainObject'
import mapKeys from 'lodash/mapKeys'
import pickBy from 'lodash/pickBy'
import reduce from 'lodash/reduce'
import set from 'lodash/set'
import snakeCase from 'lodash/snakeCase'
import some from 'lodash/some'

// -- parseBody()

/*
 * Sentry filters out keys and strings when it sees the word "password"
 *  and other fun stuff.
 * By parsing the body from a json string back into an object, sentry
 *  will only filter out individual keys and strings as opposed to the
 *  entire string; this gets us better debugging info!
 */

export const parseBody = (obj) =>
  obj.body // if `obj` has `.body`
    ? {
        ...obj, // combine `obj`
        ...{body: JSON.parse(obj.body)}, // with {body: [string=>obj]}
      }
    : obj // otherwise return `obj` unmolested

// -- resolveJson()

/*
 * `resolveJson` creates a clojure which resolves `json` property on `response` and then reattaches static
 * information about the response such as the `status` and `statusText` to the result, which
 *  can now be appended with `.then`s that consume the JSON data.
 */

export const resolveJson = (response) =>
  response.json().then((json) => ({
    json,
    status: response.status,
    statusText: response.statusText,
  }))

// -- onStatus()

// convert 'OK' to '2\d\d'
const okToRegexString = (obj) => mapKeys(obj, (value, key) => (key === 'OK' ? '2\\d\\d' : key))

// convert 'REDIRECT' to '3\d\d'
const redirectToRegexString = (obj) =>
  mapKeys(obj, (value, key) => (key === 'REDIRECT' ? '3\\d\\d' : key))

// convert 'CLIENT_ERROR' to '4\d\d'
const clientErrorToRegexString = (obj) =>
  mapKeys(obj, (value, key) => (key === 'CLIENT_ERROR' ? '4\\d\\d' : key))

// convert 'SERVER_ERROR' to '5\d\d'
const serverErrorToRegexString = (obj) =>
  mapKeys(obj, (value, key) => (key === 'SERVER_ERROR' ? '5\\d\\d' : key))

// convert 'DEFAULT' to '\d*'
const defaultToRegexString = (obj) =>
  mapKeys(obj, (value, key) => (key === 'DEFAULT' ? '\\d*' : key))

/*
 * `onStatus` accepts an object like:
 *   {
 *     OK: response => console.log(response.json),
 *     302: () => console.log('Cached response returned.'),
 *     DEFAULT: () => console.log(`An unexpected error occured: ${statusText}.`),
 *   }
 *
 * The response.status is compared against the keys of the object, which are treated as regular expressions
 *  or keywords that are translated to regular expressions. Upon the first match, the corresponing function will
 *  be executed with the response passed in. I REPEAT, ONLY THE FIRST MATCH IS EXECUTED.
 */

export const onStatus = (obj) => (response) => {
  const fn = flow([
    okToRegexString,
    redirectToRegexString,
    clientErrorToRegexString,
    serverErrorToRegexString,
    defaultToRegexString,
    (x) => find(x, (value, key) => new RegExp(key).exec(response.status)), // potentially returns a function
  ])(obj)
  return isFunction(fn) ? fn(response) : response // which `response` is passed into
}

// -- shim()

// configure flow functions from helper functions
export const ebb =
  (fn, ...args) =>
  (first) =>
    fn(first, ...args)

// get a property, but throw an error if it doesn't exist
export const get = (obj, path) => {
  if (!has(obj, path)) throw new Error(`\`${path}\` path not found. Did you execute resolveJson()?`)
  return obj[path]
}

/*
 * `shim` passes an object into a shim function and then re-attaches it to the response object:
 *   so it can continue to be used with `onStatus`.
 *
 *  Example:
 *    IN: {status: 200, json: {data: {meow: 'cat'}}}
 *    // shim function removes `data` property level
 *    OUT: {status: 200, json: {meow: 'cat'}}
 */
export const shim = (fn, path) => (response) => ({
  ...response,
  ...set({}, path, fn(get(response, path))),
})

export const shimJson = (fn) => shim(fn, 'json')

/*
 * `logBadResponse` logs a console error for any status code that's
 *   A) in the 4xx or 5xx range and
 *   B) not excluded by the developer
 */

export const logBadResponse = (arr) => (response) => {
  if (
    // ignore any status codes in the arrray passed in
    !some(arr, (statusToExclude) => statusToExclude === response.status) &&
    // subsequently, respect any status code in the 400 or 500 range
    response.status >= 400 &&
    response.status < 600
  ) {
    console.error('Bad Response: ', response)
  }

  // pass the response through
  return response
}

// -- Casing

/**
 * Returns the given obj, with camelCased keys
 * @param {Object} obj: any object - could be flat - could have deeply nested arrays of objects
 * @return {Object} Returns object with all keys, flat and inside deeply nested arrays in camelCase
 */
export const camelCaseObjectKeys = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((each) => camelCaseObjectKeys(each))
  }

  if (isPlainObject(obj)) {
    return Object.keys(obj).reduce(
      (acc, key) => ({
        ...acc,
        [camelCase(key)]: camelCaseObjectKeys(obj[key]),
      }),
      {},
    )
  }
  return obj
}

export const snakeCaseObjectKeys = (obj) =>
  reduce(
    obj,
    (acc, value, key) => ({
      ...acc,
      [snakeCase(key)]: isPlainObject(value) ? snakeCaseObjectKeys(value) : value,
    }),
    {},
  )

// -- Renaming

export const shallowRenameKeys = (obj, keyMap) => mapKeys(obj, (value, key) => keyMap[key] || key)

export const deepRenameKeys = (obj, keyMap) =>
  reduce(
    obj,
    (acc, value, key) => ({
      ...acc,
      [keyMap[key] || key]: isPlainObject(value) ? deepRenameKeys(value, keyMap) : value,
    }),
    {},
  )

// -- Omitting

export const shallowOmitKeys = (obj, keyMap) => pickBy(obj, (_, key) => !includes(keyMap, key))

export const deepOmitKeys = (obj, keyArr) =>
  reduce(
    obj,
    (acc, value, key) =>
      includes(keyArr, key)
        ? acc
        : {
            ...acc,
            [key]: isPlainObject(value) ? deepOmitKeys(value, keyArr) : value,
          },
    {},
  )
